<template>
  <DsPageContent>
    <!-- Hero with Media -->
    <DsHeroWithMedia
      v-if="content.hero"
      theme="grey"
      :screen="screen"
      :title="content.hero.title"
      :image="content.hero.image"
      :links="content.hero.links"
      :eyebrow="content.hero.eyebrow"
      vertical
      align-center
    >
      <template
        v-if="content.hero.description"
        #description
      >
        <RichText
          :data="content.hero.description"
          class="ds-text-medium"
        />
      </template>
    </DsHeroWithMedia>
    <DsSectionGroup
      :title="content.contentBlock1.title"
      :subtitle="content.contentBlock1.description"
      centered
      with-title-size-large
    >
      <DsMediaTextBlock
        v-for="(item, index) in content.contentBlock1.items"
        :key="item.title"
        :orientation="index % 2 === 1 ? 'left' : 'right'"
        :title="item.title"
        :image="item.image"
        :screen="screen"
        :class="index !== 0 && 'ds-padding-top-48 ds-md-padding-top-96'"
      >
        <RichText
          :data="item.description"
          class="ds-list-style-checked ds-list-style-checked--green ds-list-font-default"
        />
      </DsMediaTextBlock>
    </DsSectionGroup>
    <DsSection :with-space="false">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsDivider />
        </div>
      </div>
    </DsSection>
    <DsSection
      :with-space-bottom="false"
      class="ds-text-align-center ds-padding-bottom-48"
    >
      <h2
        class="ds-heading"
        v-text="content.contentBlock2.title"
      />
      <RichText
        class="ds-margin-top-12"
        :data="content.contentBlock2.description"
      />
    </DsSection>
    <DsSectionImage
      v-if="content.contentBlock2.video"
      theme-variant="light-grey"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsVideoPlayer
            :aria-label-play="labels.play"
            :aria-label-close="labels.close"
            v-bind="content.contentBlock2.video"
            :screen="screen"
            class="ds-width-full"
          />
        </div>
      </div>
    </DsSectionImage>
    <DsSection
      v-if="content.contentBlock3"
      theme="grey"
    >
      <DsMediaTextBlock
        :title="content.contentBlock3.title"
        :image="content.contentBlock3.image"
        :screen="screen"
      >
        <RichText :data="content.contentBlock3.description" />
      </DsMediaTextBlock>
    </DsSection>
    <DsQuote
      v-if="content.quote1"
      :contained="false"
      :logo="content.quote1.logo"
      :text="content.quote1.quote"
      :author="content.quote1.author"
      :extra-info="content.quote1.role"
    />
    <template v-if="content.usps">
      <DsSection :with-space="false">
        <div class="ds-grid">
          <div class="ds-grid__col">
            <DsDivider />
          </div>
        </div>
      </DsSection>
      <DsSectionGroup
        centered
        :title="content.usps.title"
        :subtitle="content.usps.description"
      >
        <DsGrid
          class="ds-margin-top-24"
          :column-width="{ small: 12, medium: 4, large: 4 }"
          :items="content.usps.items"
        >
          <template #default="{ item, index }">
            <DsCardTextIcon
              v-if="item"
              :icon="item.icon"
              :title="item.title"
              alignment="center"
              :class="{ 'ds-margin-top-48 ds-md-margin-top-0': index !== 0 }"
            />
          </template>
        </DsGrid>
      </DsSectionGroup>
    </template>
    <DsSection
      v-if="content.image"
      :with-space-top="false"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsImageElement
            :image="content.image"
            :screen="screen"
          />
        </div>
      </div>
    </DsSection>
    <DsSection
      v-if="content.contentBlock4"
      theme="grey"
    >
      <DsMediaTextBlock
        :title="content.contentBlock4.title"
        :image="content.contentBlock4.image"
        :screen="screen"
        orientation="bottom"
        center-aligned
        :link="content.contentBlock4.link"
      >
        <RichText :data="content.contentBlock4.description" />
      </DsMediaTextBlock>
    </DsSection>
    <DsQuote
      v-if="content.quote2"
      :contained="false"
      :logo="content.quote2.logo"
      :text="content.quote2.quote"
      :author="content.quote2.author"
      :extra-info="content.quote2.role"
      theme="grey"
    />
    <DsSection v-if="content.logoBar">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsLogoBar
            :title="content.logoBar.title"
            :link="content.logoBar.link"
            :items="content.logoBar.items"
            align-center
          >
            <template #description>
              <RichText :data="content.logoBar.description" />
            </template>
          </DsLogoBar>
        </div>
      </div>
    </DsSection>
    <DsCallToAction
      v-if="content.cta"
      :title="content.cta.title"
      :text="content.cta.text"
      :primary-link="content.cta.primaryLink"
      :secondary-link="content.cta.secondaryLink"
    />
  </DsPageContent>
</template>

<script setup>
import {
  DsCallToAction,
  DsCardTextIcon,
  DsDivider,
  DsGrid,
  DsHeroWithMedia,
  DsImageElement,
  DsLogoBar,
  DsMediaTextBlock,
  DsPageContent,
  DsQuote,
  DsSection,
  DsSectionGroup,
  DsSectionImage,
  DsVideoPlayer
} from 'awds'
import { PageProductPlugins } from '#layers/core/data-layer/definitions/products'
import RichText from '#layers/core/components/RichText.vue'

definePageMeta({
  pageTransition: {
    name: 'page-fade'
  }
})

const { locale: localeRef } = useI18n()
const { screen } = useScreen()
const { onBeforeAdyenData, onAfterAdyenData, useStandardAsyncData, content, labels } = useMainPageData()
const { setNotification } = useNotificationBanner()

await onBeforeAdyenData({
  withRoutes: true,
  globalDataContentType: PageProductPlugins.contentType
})

const { data } = await useAsyncData(`product-plugins-${localeRef.value}`, async () => {
  const pageData = await useStandardAsyncData(PageProductPlugins)
  if (!pageData || !Object.keys(pageData).length) {
    return null
  }
  return pageData
})

await onAfterAdyenData(data.value)

onBeforeMount(() => {
  setNotification(data.value?.pageData?.content?.notificationBar)
})
</script>
